import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { buscarNumeroNegocioBaserow } from '../../utils/buscarNumeroNegocioBaserow';
import { FileInputWithCheckbox } from '../FileInputCheckBox';
import axios from 'axios';
import { createClient } from '@supabase/supabase-js';
import { saveAs } from 'file-saver';

const FormReiteracionGastos = () => {
  const { t } = useTranslation();
  const [numeroNegocio, setNumeroNegocio] = useState('');
  const [clienteData, setClienteData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Estado para autocompletar y editar campos
  const [autoFill, setAutoFill] = useState({
    nombres: ['', ''],
    banco: '',
    numeroProtocolo: '',
    fechaInicioPrestamo: '',
    fechaReclamacion: '',
    notario: '',
  });

  // Estado para archivos (unificado)
  const [archivos, setArchivos] = useState({
    dniFrontFile: null,
    dniBackFile: null,
    dniFrontFileAdicional: null,
    dniBackFileAdicional: null,
    notariaFile: null,
    registroFile: null,
    gestoriaFile: null,
    tasacionFile: null,
    notariaFile2: null,
    registroFile2: null,
    gestoriaFile2: null,
    tasacionFile2: null,
  });

  // Función utilitaria para descargar y setear archivos
  const fetchAndSetFile = async (doc, key, stateKey, defaultName) => {
    if (doc[key] && doc[key][0]?.url) {
      const response = await fetch(doc[key][0].url);
      const blob = await response.blob();
      setArchivos(prev => ({
        ...prev,
        [stateKey]: {
          file: new File([blob], doc[key][0].filename || defaultName, { type: blob.type }),
          name: doc[key][0].filename || defaultName
        }
      }));
    } else {
      setArchivos(prev => ({ ...prev, [stateKey]: null }));
    }
  };

  const handleBuscar = async () => {
    if (!numeroNegocio) {
      toast.error(t('Introduce un número de negocio'));
      return;
    }
    setLoading(true);
    toast.loading(t('Buscando número de negocio...'));
    // 1. Obtener la fecha de reclamación desde el endpoint externo
    let fechaReclamacionFromApi = '';
    try {
      const resp = await axios.get('https://n8n-615087458109.europe-southwest1.run.app/webhook/5b6dba31-3395-4b45-b8b0-c961f4f32026', {
        params: { numero_negocio: numeroNegocio }
      });
      // Buscar la fecha en la respuesta (puede estar en data[1]["properties.fecha_del_envio_al_banco.value"])
      if (resp.data && resp.data[1] && resp.data[1]["properties.fecha_del_envio_al_banco.value"]) {
        // Convertir de MM/DD/YYYY a YYYY-MM-DD
        const fecha = resp.data[1]["properties.fecha_del_envio_al_banco.value"];
        const partes = fecha.split('/');
        if (partes.length === 3) {
          const [mes, dia, anio] = partes;
          fechaReclamacionFromApi = `${anio}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
        }
      }
    } catch (err) {
      // Si falla, no autocompleta la fecha
      console.log(err);
      fechaReclamacionFromApi = '';
    }
    // 2. Buscar en Baserow como antes
    const datos = await buscarNumeroNegocioBaserow(numeroNegocio);
    toast.dismiss();
    setLoading(false);
    if (datos && datos.length > 0) {
      setClienteData(datos);
      setAutoFill({
        nombres: [datos[0]['Nombre Y Apellidos'] || '', datos[2]?.['Nombre Y Apellidos'] || ''],
        banco: datos[1][0]?.['Banco'] || '',
        numeroProtocolo: datos[1][0]?.['Numero Protocolo'] || '',
        fechaInicioPrestamo: datos[1][0]?.['Fecha Inicio Prestamo'] || '',
        fechaReclamacion: fechaReclamacionFromApi || datos[1][0]?.['Fecha Reclamacion Extrajudicial'] || '',
        notario: datos[1][0]?.['Notario'] || '',
      });
      // Cliente 1
      const doc1 = datos[1][0];
      if (doc1) {
        await fetchAndSetFile(doc1, 'Documento DNI Front', 'dniFrontFile', 'dni_front.jpg');
        await fetchAndSetFile(doc1, 'Documento DNI Back', 'dniBackFile', 'dni_back.jpg');
        await fetchAndSetFile(doc1, 'Documento Notaría', 'notariaFile', 'notaria.pdf');
        await fetchAndSetFile(doc1, 'Documento Registro', 'registroFile', 'registro.pdf');
        await fetchAndSetFile(doc1, 'Documento Gestoría', 'gestoriaFile', 'gestoria.pdf');
        await fetchAndSetFile(doc1, 'Documento Tasación', 'tasacionFile', 'tasacion.pdf');
        await fetchAndSetFile(doc1, 'Documento DNI Front Adicional', 'dniFrontFileAdicional', 'dni_front2.jpg');
        await fetchAndSetFile(doc1, 'Documento DNI Back Adicional', 'dniBackFileAdicional', 'dni_back2.jpg');
        await fetchAndSetFile(doc1, 'Documento Notaría Adicional', 'notariaFile2', 'notaria2.pdf');
        await fetchAndSetFile(doc1, 'Documento Registro Adicional', 'registroFile2', 'registro2.pdf');
        await fetchAndSetFile(doc1, 'Documento Gestoría Adicional', 'gestoriaFile2', 'gestoria2.pdf');
        await fetchAndSetFile(doc1, 'Documento Tasación Adicional', 'tasacionFile2', 'tasacion2.pdf');
      }
      // Cliente 2 (si existe)
      const doc2 = datos[1][1];
      if (doc2) {
        await fetchAndSetFile(doc2, 'Documento DNI Front Adicional', 'dniFrontFileAdicional', 'dni_front2.jpg');
        await fetchAndSetFile(doc2, 'Documento DNI Back Adicional', 'dniBackFileAdicional', 'dni_back2.jpg');
        await fetchAndSetFile(doc2, 'Documento Notaría Adicional', 'notariaFile2', 'notaria2.pdf');
        await fetchAndSetFile(doc2, 'Documento Registro Adicional', 'registroFile2', 'registro2.pdf');
        await fetchAndSetFile(doc2, 'Documento Gestoría Adicional', 'gestoriaFile2', 'gestoria2.pdf');
        await fetchAndSetFile(doc2, 'Documento Tasación Adicional', 'tasacionFile2', 'tasacion2.pdf');
      } else {
        setArchivos(prev => ({
          ...prev,
          dniFrontFileAdicional: null,
          dniBackFileAdicional: null,
          notariaFile2: null,
          registroFile2: null,
          gestoriaFile2: null,
          tasacionFile2: null,
        }));
      }
      toast.success(t('Datos encontrados'));
    } else {
      setClienteData(null);
      setAutoFill({
        nombres: ['', ''], banco: '', numeroProtocolo: '', fechaInicioPrestamo: '', fechaReclamacion: '', notario: ''
      });
      setArchivos({
        dniFrontFile: null,
        dniBackFile: null,
        dniFrontFileAdicional: null,
        dniBackFileAdicional: null,
        notariaFile: null,
        registroFile: null,
        gestoriaFile: null,
        tasacionFile: null,
        notariaFile2: null,
        registroFile2: null,
        gestoriaFile2: null,
        tasacionFile2: null,
      });
      toast.error(t('No se encontraron datos para ese número de negocio'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    Object.entries(autoFill).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v, i) => formData.append(`${key}[${i}]`, v));
      } else {
        formData.append(key, value ?? '');
      }
    });

    const archivosEnviar = archivos;
    Object.entries(archivosEnviar).forEach(([key, obj]) => {
      if (obj && obj.file) {
        formData.append(key, obj.file, obj.name || obj.file.name);
      }
    });

    //Añadir al formData el tipo de formulario
    formData.append('tipo_formulario', 'reiteracion_gastos');

    try {
      const supabaseClient = createClient(
        'https://uoclqlfzojdjrhcqhgef.supabase.co',
        'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InVvY2xxbGZ6b2pkanJoY3FoZ2VmIiwicm9sZSI6ImFub24iLCJpYXQiOjE2OTc3MDEzMDMsImV4cCI6MjAxMzI3NzMwM30.6VY6hK2MWHngYFt3AP9MkV_1hC0xOIuX0CbYIsC5DT0'
      );
      const { data } = await supabaseClient.auth.getSession();
      const access_token = data.session.access_token;
      const resp = await axios.post('https://api.office.coventia.es/enviar-datos-reiteracion-gastos/', formData, {
        headers: {
          'Authorization': access_token
        }
      });
      console.log(resp.data);
      if (resp.data && resp.data.documentos_generados && Array.isArray(resp.data.documentos_generados)) {
        const baseUrl = 'https://api.office.coventia.es/media/generated/';
        for (const fileName of resp.data.documentos_generados) {
          const url = baseUrl + fileName;
          const fileResp = await fetch(url);
          const blob = await fileResp.blob();
          saveAs(blob, fileName);
        }
        toast.success(t('Archivos descargados correctamente'));
      } else {
        toast.success(t('Datos enviados correctamente'));
      }
    } catch (error) {
      console.error(error);
      toast.error(t('Error al enviar los datos'));
    }
  };

  return (
    <div>

      <div className="flex flex-col md:flex-row items-center justify-between space-y-3 md:space-y-0 md:space-x-4 p-4">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
          {t("🖋  Rellenando documento de reiteración de gastos")}
        </h2>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-10 w-full">
          <label htmlFor="numero_negocio_reiteracion" className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2 mx-auto w-3/">
            {t('Buscar negocio')}
          </label>
          <div className="flex flex-col w-full max-w-4xl mx-auto">
            <div className="flex mb-8 w-full">
              <div className="relative w-full flex shadow-lg rounded-lg">
                <input
                  id="numero_negocio_reiteracion"
                  name="numero_negocio_reiteracion"
                  type="text"
                  className="w-full px-4 py-3 rounded-l-lg border-0 ring-1 ring-gray-200 focus:ring-2 focus:ring-primary-500 transition-all duration-200 dark:bg-gray-800 dark:ring-gray-700 dark:text-white"
                  value={numeroNegocio}
                  onChange={e => setNumeroNegocio(e.target.value)}
                  placeholder={t('Buscar un número de negocio...')}
                  disabled={loading}
                />
                <button
                  type="button"
                  disabled={loading}
                  className="px-6 rounded-r-lg bg-primary-600 hover:bg-primary-700 transition-colors duration-200 flex items-center justify-center gap-2 text-white font-medium dark:bg-blue-600 dark:hover:bg-blue-700"
                  onClick={handleBuscar}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                  </svg>
                  {t('Buscar')}
                </button>
              </div>
            </div>

            {clienteData && (
              <form onSubmit={handleSubmit} className="space-y-6 animate-fadeIn">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
                  <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {t('Datos Personales')}
                    </h3>
                  </div>
                  <div className="p-6 overflow-x-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('Nombre cliente 1')}</label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 rounded border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          value={autoFill.nombres[0]}
                          onChange={e => setAutoFill({ ...autoFill, nombres: [e.target.value, autoFill.nombres[1]] })}
                          required
                        />
                      </div>
                      {autoFill.nombres[1] && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('Nombre cliente 2')}</label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 rounded border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            value={autoFill.nombres[1]}
                            onChange={e => setAutoFill({ ...autoFill, nombres: [autoFill.nombres[0], e.target.value] })}
                            required
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Card de datos de la reclamación */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
                  <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {t('Datos de la reclamación')}
                    </h3>
                  </div>
                  <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('Banco')}</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 rounded border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        value={autoFill.banco}
                        onChange={e => setAutoFill({ ...autoFill, banco: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('Número de Protocolo')}</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 rounded border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        value={autoFill.numeroProtocolo}
                        onChange={e => setAutoFill({ ...autoFill, numeroProtocolo: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('Fecha Inicio Préstamo')}</label>
                      <input
                        type="date"
                        className="w-full px-3 py-2 rounded border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        value={autoFill.fechaInicioPrestamo}
                        onChange={e => setAutoFill({ ...autoFill, fechaInicioPrestamo: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('Fecha Reclamación')}</label>
                      <input
                        type="date"
                        className="w-full px-3 py-2 rounded border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        value={autoFill.fechaReclamacion}
                        onChange={e => setAutoFill({ ...autoFill, fechaReclamacion: e.target.value })}
                        required
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('Notario')}</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 rounded border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        value={autoFill.notario}
                        onChange={e => setAutoFill({ ...autoFill, notario: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Card de documentos */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden p-6 space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('Documentos')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FileInputWithCheckbox
                      name="dni_front"
                      id="dni_front"
                      label={t('DNI Frontal cliente 1')}
                      setFile={file => setArchivos(prev => ({ ...prev, dniFrontFile: file }))}
                      file={archivos.dniFrontFile}
                      disabled={loading}
                      showAdditionalOption={false}
                    />
                    <FileInputWithCheckbox
                      name="dni_back"
                      id="dni_back"
                      label={t('DNI Trasero cliente 1')}
                      setFile={file => setArchivos(prev => ({ ...prev, dniBackFile: file }))}
                      file={archivos.dniBackFile}
                      disabled={loading}
                      showAdditionalOption={false}
                    />
                    <FileInputWithCheckbox
                      name="notaria"
                      id="notaria"
                      label={t('Documento Notaría cliente 1')}
                      setFile={file => setArchivos(prev => ({ ...prev, notariaFile: file }))}
                      file={archivos.notariaFile}
                      disabled={loading}
                      showAdditionalOption={false}
                    />
                    <FileInputWithCheckbox
                      name="registro"
                      id="registro"
                      label={t('Documento Registro cliente 1')}
                      setFile={file => setArchivos(prev => ({ ...prev, registroFile: file }))}
                      file={archivos.registroFile}
                      disabled={loading}
                      showAdditionalOption={false}
                    />
                    <FileInputWithCheckbox
                      name="gestoria"
                      id="gestoria"
                      label={t('Documento Gestoría cliente 1')}
                      setFile={file => setArchivos(prev => ({ ...prev, gestoriaFile: file }))}
                      file={archivos.gestoriaFile}
                      disabled={loading}
                      showAdditionalOption={false}
                    />
                    <FileInputWithCheckbox
                      name="tasacion"
                      id="tasacion"
                      label={t('Documento Tasación cliente 1')}
                      setFile={file => setArchivos(prev => ({ ...prev, tasacionFile: file }))}
                      file={archivos.tasacionFile}
                      disabled={loading}
                      showAdditionalOption={false}
                    />
                    {autoFill.nombres[1] && (
                      <>
                        <FileInputWithCheckbox
                          name="dni_front2"
                          id="dni_front2"
                          label={t('DNI Frontal cliente 2')}
                          setFile={file => setArchivos(prev => ({ ...prev, dniFrontFileAdicional: file }))}
                          file={archivos.dniFrontFileAdicional}
                          disabled={loading}
                          showAdditionalOption={false}
                        />
                        <FileInputWithCheckbox
                          name="dni_back2"
                          id="dni_back2"
                          label={t('DNI Trasero cliente 2')}
                          setFile={file => setArchivos(prev => ({ ...prev, dniBackFileAdicional: file }))}
                          file={archivos.dniBackFileAdicional}
                          disabled={loading}
                          showAdditionalOption={false}
                        />
                        <FileInputWithCheckbox
                          name="notaria2"
                          id="notaria2"
                          label={t('Documento Notaría cliente 2')}
                          setFile={file => setArchivos(prev => ({ ...prev, notariaFile2: file }))}
                          file={archivos.notariaFile2}
                          disabled={loading}
                          showAdditionalOption={false}
                        />
                        <FileInputWithCheckbox
                          name="registro2"
                          id="registro2"
                          label={t('Documento Registro cliente 2')}
                          setFile={file => setArchivos(prev => ({ ...prev, registroFile2: file }))}
                          file={archivos.registroFile2}
                          disabled={loading}
                          showAdditionalOption={false}
                        />
                        <FileInputWithCheckbox
                          name="gestoria2"
                          id="gestoria2"
                          label={t('Documento Gestoría cliente 2')}
                          setFile={file => setArchivos(prev => ({ ...prev, gestoriaFile2: file }))}
                          file={archivos.gestoriaFile2}
                          disabled={loading}
                          showAdditionalOption={false}
                        />
                        <FileInputWithCheckbox
                          name="tasacion2"
                          id="tasacion2"
                          label={t('Documento Tasación cliente 2')}
                          setFile={file => setArchivos(prev => ({ ...prev, tasacionFile2: file }))}
                          file={archivos.tasacionFile2}
                          disabled={loading}
                          showAdditionalOption={false}
                        />
                      </>
                    )}
                  </div>
                </div>
                <div className="col-span-2 sm:col-span-2 flex justify-end">
                  <button type="submit" className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-4 focus:ring-primary-300">
                    {t('Siguiente')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormReiteracionGastos; 